import { SaveActionType } from '~/core/constants';
import { CustomerInfo } from '~/core/models/customer-info.model';
import { Estimate } from '~/core/models/estimate.model';
import { SaveAction } from '~/core/models/save-action.model';
import { replacerV3 } from '~/core/utils/estimate.utils';

export interface SaveEstimateItem {
  productId: number;
  quantity: number;
}

/**
 * CommonEstimateDto includes elements common to both the POST and GET of the estimate APIs
 */
interface CommonEstimateDto {
  code: string; // this is the guid/token
  storeId: number;
  customerEmail: string;
  customerName: string;
  customerAddress1: string;
  customerAddress2?: string;
  customerCity: string;
  customerState: string;
  customerZip: string;
  customerPhone: string;
  customerAlternatePhone: string;
  sendCustomerEmail: boolean;
  estimateData: Estimate;
  note: string;
  ticketNumber?: string;
}

export interface SaveEstimateDto extends CommonEstimateDto {
  prodQteGrpID: number;
  estimateItems: SaveEstimateItem[];
  estimateImage: string;
  isEE3: boolean;
  saveAction: SaveActionType;
  userToken?: string;
  discountOverride?: number;
}

export interface SaveEstimateResponse {
  estimateNumber: string;
  code: string;
  paymentUrl: string;
}

export interface Revision {
  estimateNumber: string;
  lastModified: string; //"2022-11-21T15:45:49.899Z",
  code: string;
}

export interface GetEstimateDto extends CommonEstimateDto {
  estimateNumber: string;
  status: string;
  paymentUrl?: string;
  quoteDate: string;
  quoteExpirationDate: string;
  revisions: Revision[];
  saveActions: SaveAction[] | null;
  estimateDrawingUrl: string;
}

export const makeSaveEstimateDto = ({
  customer,
  estimateData,
  estimateImage,
  lineItems,
  saveAction,
  code,
  ticketNumber,
  userToken,
  discountOverride,
}: {
  customer: CustomerInfo;
  estimateData: Estimate;
  estimateImage: string;
  lineItems: SaveEstimateItem[];
  saveAction: SaveActionType;
  code?: string;
  ticketNumber?: string;
  userToken?: string;
  discountOverride?: number;
}): SaveEstimateDto => ({
  code: code ? code : '', // must be empty string instead of null
  customerEmail: customer.email,
  estimateData: estimateData,
  estimateImage: estimateImage,
  estimateItems: lineItems,
  prodQteGrpID: estimateData.qteGrpID ?? 0,
  storeId: estimateData.storeId || 0, //TODO Fix the types, should never be zero
  customerName: customer.customerName,
  customerAddress1: customer.address1,
  customerAddress2: customer.address2,
  customerCity: customer.city,
  customerState: customer.state,
  customerZip: customer.zip,
  customerPhone: customer.phone,
  customerAlternatePhone: customer.alternatePhone,
  sendCustomerEmail: customer.sendCustomerEmail ?? true,
  saveAction: saveAction,
  note: estimateData.productNotes
    .map((value) =>
      [value.productDescription.trim() + ':', value.note.trim()].join('\n')
    )
    .join('\n\n'),
  ticketNumber,
  userToken,
  discountOverride,
  isEE3: true,
});

export const makeSaveEstimateFormData = (args: {
  customer: CustomerInfo;
  estimateData: Estimate;
  estimateImageBase64: string;
  lineItems: SaveEstimateItem[];
  saveAction: SaveActionType;
  code?: string;
  ticketNumber?: string;
  userToken?: string;
  discountOverride?: number;
}): FormData => {
  const {
    customer,
    estimateData,
    estimateImageBase64,
    lineItems,
    saveAction,
    code,
    ticketNumber,
    userToken,
    discountOverride,
  } = args;
  const formData = new FormData();

  // create the estimateDto using the original function, then we'll explode it afterward
  const estimateDto = makeSaveEstimateDto({
    customer,
    estimateData,
    estimateImage: '', // just a placeholder, we'll do the image separately
    lineItems,
    saveAction,
    code,
    ticketNumber,
    userToken,
    discountOverride,
  });

  Object.entries(estimateDto).forEach(([key, value]) => {
    // ignore estimateImage from here, it's blank anyhow
    if (key === 'estimateImage') {
      return;
    }
    if (key === 'estimateData') {
      // handle the json element (estimateData should be the only one)
      formData.append(key, JSON.stringify(value, replacerV3));
    } else if (Array.isArray(value)) {
      // All arrays should be broken out into individual rows and elements
      value.forEach((item: SaveEstimateItem, index: number) => {
        Object.entries(item).forEach(([itemKey, itemValue]) => {
          formData.append(`${key}[${index}].${itemKey}`, String(itemValue));
        });
      });
    } else if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  const imageBlob = new Blob(
    [Uint8Array.from(atob(estimateImageBase64), (c) => c.charCodeAt(0))],
    {
      type: 'image/png',
    }
  );

  formData.append('estimateImage', imageBlob, 'estimate.png');

  return formData;
};

export type EstimateStatus = {
  imeStatus: string | null;
  imeMessage: string | null;
  status: string | null;
  paymentUrl: string | null;
};

export const initialEstimateStatus = {
  imeStatus: null,
  imeMessage: null,
  status: null,
  paymentUrl: null,
};

export const createEstimateStatus = (
  init?: Partial<EstimateStatus>
): EstimateStatus => ({
  imeStatus: init?.imeStatus ?? null,
  imeMessage: init?.imeMessage ?? null,
  status: init?.status ?? null,
  paymentUrl: init?.paymentUrl ?? null,
});
